/* @flow */
import Raven from "raven-js";
import type { Geofence, GeofenceCrossing } from "nexia_mobile/platforms/base";

function callBridge(
  prefix: string,
  command: string,
  body: Blob | Object = {},
  search: string = ""
): Promise<Object> {
  let argsString;
  if (body instanceof Blob) {
    argsString = `Blob(${body.size})`;
  } else {
    argsString = JSON.stringify(body);
  }

  Raven.captureBreadcrumb({
    category: "iOS Bridge",
    level: "info",
    message: `${prefix}/${command}`,
    data: {
      args:
        argsString.length > 1024
          ? "[args hidden because size exceeded 1024 bytes]"
          : argsString,
      search,
    },
  });

  type PlatformBridgeResponse =
    | { error: true, errorDescription: string, errorFailureReason?: string }
    | { error: false, data: any };

  return fetch(`http://localhost:4400/${prefix}/${command}${search}`, {
    method: "POST",
    headers: {
      "Content-Type":
        body instanceof Blob ? "application/octet-stream" : "application/json",
    },
    body: body instanceof Blob ? body : argsString,
  })
    .then((response) => response.json())
    .then((jsonResponse: PlatformBridgeResponse) => {
      if (jsonResponse.error) {
        throw new Error(jsonResponse.errorDescription);
      } else {
        return jsonResponse.data;
      }
    });
}

// These all pass through to callBridge, but they have type info; callBridge
// returns Promise<any>, so these refine that into eg.
// Promise<{ result: boolean }> so that the types written in the Objective-C
// code are available to the rest of the world.
export default {
  misc: {
    keepScreenOn: (): Promise<{||}> => callBridge("misc", "keepScreenOn"),
    allowScreenDimming: (): Promise<{||}> =>
      callBridge("misc", "allowScreenDimming"),
    obtainPushNotificationToken: (): Promise<{||}> =>
      callBridge("misc", "obtainPushNotificationToken"),
    resetAppBadge: (): Promise<{||}> => callBridge("misc", "resetAppBadge"),
    saveImage: (
      image: string
    ): Promise<{ hasPermission: boolean, message: string }> =>
      callBridge("misc", "saveImage", { image }),
    sendGoogleAnalyticsEvent: (
      category: string,
      label: string,
      action: string,
      value: number
    ): Promise<{||}> =>
      callBridge("misc", "sendGoogleAnalyticsEvent", {
        category,
        label,
        action,
        value,
      }),
    hasAppInstalled: (name: string): Promise<{ result: boolean }> =>
      callBridge("misc", "hasAppInstalled", { name }),
    hideSoftKeyboard: (): Promise<{||}> =>
      callBridge("misc", "hideSoftKeyboard"),
    openAppPermissionSettings: (): Promise<{||}> =>
      callBridge("misc", "openAppPermissionSettings"),
    isVoiceOverEnabled: (): Promise<{ result: boolean }> =>
      callBridge("misc", "isVoiceOverEnabled"),
    encryptStringFor724OpenAuth: (
      key: string,
      input: string
    ): Promise<{ result: string }> =>
      callBridge("misc", "encryptStringFor724OpenAuth", { key, input }),
  },
  localStorage: {
    getAllStoredValues: (): Promise<{ [key: string]: string }> =>
      callBridge("localStorage", "getAllStoredValues"),
    setStoredValue: (key: string, value: string): Promise<{||}> =>
      callBridge("localStorage", "setStoredValue", { key, value }),
    clearStoredValue: (key: string): Promise<{||}> =>
      callBridge("localStorage", "clearStoredValue", { key }),
    clearStoredValues: (): Promise<{||}> =>
      callBridge("localStorage", "clearStoredValues"),
  },
  vognition: {
    checkMicrophonePermissions: (): Promise<{|
      status: "granted" | "denied" | "undetermined",
    |}> => callBridge("vognition", "checkMicrophonePermissions"),
    runVognitionListener: (): Promise<{||}> =>
      callBridge("vognition", "runVognitionListener"),
    stopVognitionListener: (): Promise<{||}> =>
      callBridge("vognition", "stopVognitionListener"),
    speakString: (message: string): Promise<{||}> =>
      callBridge("vognition", "speakString", { message }),
    setCurrentVognitionHomeWithProfile: (
      houseId: string,
      encodedProfile: string
    ): Promise<{||}> =>
      callBridge("vognition", "setCurrentVognitionHomeWithProfile", {
        houseId,
        encodedProfile,
      }),
  },
  geofence: {
    checkLocationPermissions: (): Promise<{ result: boolean }> =>
      callBridge("geofence", "checkLocationPermissions"),
    areLocationServicesEnabled: (): Promise<{ result: boolean }> =>
      callBridge("geofence", "areLocationServicesEnabled"),
    setGeofences: (geofences: Array<Geofence>): Promise<{||}> =>
      callBridge("geofence", "setGeofences", { geofences }),
    startGeofenceService: (): Promise<{||}> =>
      callBridge("geofence", "startGeofenceService"),
    stopGeofenceService: (): Promise<{||}> =>
      callBridge("geofence", "stopGeofenceService"),
    getGeofenceCrossings: (): Promise<{ crossings: Array<GeofenceCrossing> }> =>
      callBridge("geofence", "getGeofenceCrossings"),
  },
  touchID: {
    isTouchIDAvailable: (): Promise<{
      result: boolean,
      isActuallyFaceID: boolean,
    }> => callBridge("touchID", "isTouchIDAvailable"),
    authenticateWithTouchID: (): Promise<{
      success: boolean,
      wasIntentionallyCancelled: boolean,
    }> => callBridge("touchID", "authenticateWithTouchID"),
    cancelTouchIDAuthentication: (): Promise<{||}> =>
      callBridge("touchID", "cancelTouchIDAuthentication"),
  },
  platformAttributes: {
    getPlatformAttributes: (): Promise<{
      bundleIdentifier: string,
      appVersion: string,
      platformName: string,
      manufacturer: string,
      deviceName: string,
      model: string,
      osVersion: string,
      deviceUUID: string,
      currentLocale: string,
    }> => callBridge("platformAttributes", "getPlatformAttributes"),
  },
  http: {
    nativeHTTPRequest: (options: {
      method: string,
      url: string,
      body?: ?string,
      headers?: ?{ [key: string]: string },
    }): Promise<{ body: string, code: number }> =>
      callBridge("http", "nativeHTTPRequest", options),
  },
  temp: {
    createTemporaryFile: (
      blob: Blob,
      extension: string
    ): Promise<{ url: string }> =>
      callBridge(
        "temp",
        "createTemporaryFile",
        blob,
        `?extension=${extension}`
      ),
    removeTemporaryFile: (url: string): Promise<{||}> =>
      callBridge("temp", "removeTemporaryFile", { url }),
  },
};



// WEBPACK FOOTER //
// ./src/platforms/ios/bridge.js